import { FaroSimpleTextField } from "@components/common/faro-text-field/faro-simple-text-field";
import { GroupAutocompleteItem } from "@components/common/group-autocomplete/group-autocomplete-item";
import { GroupId } from "@custom-types/base-types";
import { ArrowDownIcon, CloseIcon } from "@flat-ui";
import { useGroups } from "@hooks/use-groups";
import { Autocomplete, Box, Paper } from "@mui/material";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { getAutocompleteStyles } from "@utils/ui-utils";
import { SyntheticEvent, useEffect, useMemo } from "react";

type Group = {
  /** The group ID */
  id: GroupId;

  /** The title of group */
  title: string;

  /** The sub title of group */
  subtitle: string;

  /** The value of group */
  value: GroupId;

  /** The image thumbnail of group */
  imageUrl?: string;
};

interface GroupProps {
  /** The function to be called when the user selects a project */
  callbackOnGroupSelected: (value: GroupId | undefined) => void;

  /** Select the company automatically */
  selectedGroupId?: GroupId;

  /** Placeholder for the input */
  placeholder?: string;

  /** Only show the groups with the role group manager */
  shouldOnlyShowGroupsWithGroupManager?: boolean;
}

/**
 * Defines the maximum items to be displayed in the autocomplete menu as suggestions.
 * The number should be low enough to fit them all at once in the screen.
 */
const MAX_ITEMS_TO_DISPLAY = 6;

/**
 * Sorts the options by label.
 */
function sortByLabel(a: Group, b: Group): number {
  return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
}

/**
 * The GroupAutoComplete component enables users to select a group from a dynamic list of options
 * based on provided company groups. It offers autocomplete functionality with custom rendering for
 * options and icons, enhancing the user experience in selecting groups effortlessly.
 */
export function GroupAutoComplete({
  callbackOnGroupSelected,
  selectedGroupId,
  placeholder,
  shouldOnlyShowGroupsWithGroupManager = false,
}: GroupProps): JSX.Element {
  const companyGroups = useGroups();
  const currentUser = useAppSelector(currentUserSelector);

  const styles = getAutocompleteStyles();

  /**
   * Reformation company groups data for stellar autocomplete component
   * Show all group options if the user is a company executive, or shouldOnlyShowGroupsWithGroupManager prop is false.
   * Otherwise, filter the groups to only show those with the role of company manager.
   */
  const groups = useMemo(() => {
    const filteredGroups =
      currentUser &&
      currentUser.role !== "COMPANY_EXECUTIVE" &&
      shouldOnlyShowGroupsWithGroupManager
        ? companyGroups.filter((group) => group.role === "COMPANY_MANAGER")
        : companyGroups;

    return filteredGroups.map((group) => {
      return {
        id: group.id,
        title: group.name,
        subtitle: "",
        value: group.id,
        imageUrl: group.imageUrl === "" ? undefined : group.imageUrl,
      };
    });
  }, [companyGroups, currentUser, shouldOnlyShowGroupsWithGroupManager]);

  useEffect(() => {
    if (groups.length === 1) {
      const groupId = groups[0].value;
      callbackOnGroupSelected(groupId);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- multiple re-renders with the callbackOnGroupSelected
  }, [groups]);

  /**
   *  Function triggered whenever an option is selected in the Autocomplete component.
   *  Calls the onGroupSelected function to fetch the members belonging to the selected group
   *  and stores them in the groupMembers state variable.
   *
   * @param _ The synthetic event that occurred.
   * @param newValue The value of the option selected in the Autocomplete. It can be an object of type Group or null.
   */
  function handleAutocompleteChange(
    _: SyntheticEvent<Element, Event>,
    newValue: Group | null
  ): void {
    if (newValue) {
      callbackOnGroupSelected(newValue.id);
    } else {
      callbackOnGroupSelected(undefined);
    }
  }

  const selectedValues = useMemo(
    () => groups.find((group) => group.id === selectedGroupId),
    [groups, selectedGroupId]
  );

  return (
    <Autocomplete
      defaultValue={groups.length === 1 ? groups[0] : undefined}
      disabled={groups.length === 1}
      data-testid="group-auto-complete-test-id"
      id="groups-new-project"
      options={groups}
      onChange={handleAutocompleteChange}
      getOptionLabel={(option) => option.title}
      sx={{ width: "100%" }}
      autoHighlight
      value={selectedValues || null}
      popupIcon={
        <Box sx={styles.iconWrapper.sx}>
          <ArrowDownIcon />
        </Box>
      }
      clearIcon={
        <Box sx={styles.iconWrapper.sx}>
          <CloseIcon />
        </Box>
      }
      isOptionEqualToValue={(option, value) => option.title === value.title}
      filterOptions={(options, state) => {
        if (!state.inputValue) {
          // If the user didn't type anything, show all the options sorted alphabetically.
          return options.sort(sortByLabel);
        }

        const lowerInputValue = state.inputValue.toLowerCase();
        const filteredOptions = options.filter(
          (option) =>
            option.title.toLowerCase().includes(lowerInputValue) ||
            option.title.toLowerCase()?.includes(lowerInputValue)
        );

        // Limit the number of displayed options (e.g., to the first 10 matches)
        return filteredOptions.slice(0, MAX_ITEMS_TO_DISPLAY).sort(sortByLabel);
      }}
      renderOption={(props, option, state) => (
        <Box
          component="li"
          {...props}
          key={`group-item-${state.index}`}
          sx={styles.option.sx}
        >
          <GroupAutocompleteItem
            title={option.title}
            image={option.imageUrl}
            inputValue={state.inputValue}
          />
        </Box>
      )}
      renderInput={(params) => (
        <FaroSimpleTextField
          {...params}
          autoFocus={false}
          variant="outlined"
          placeholder={
            placeholder ? placeholder : "Assign this project to a group..."
          }
          sx={{
            ...styles.input.sx,
            height: "38px",
          }}
        />
      )}
      noOptionsText={
        <Box component="div" sx={styles.noOption.sx}>
          No groups found!
        </Box>
      }
      slots={{
        paper: (props) => (
          <Paper {...props} sx={styles.paper.sx}>
            {props.children}
          </Paper>
        ),
      }}
    />
  );
}
